import { useState, ChangeEvent } from "react";
import {
  Box,
  Grid,
  Tabs,
  Tab,
  Typography,
  useMediaQuery,
  Theme,
  styled,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ShowBase, useRecordContext } from "react-admin";
import { useCanAccess } from "@react-admin/ra-rbac";
import ComponentShow, { ShowActions } from "../../../components/PageComponents/ComponentShow";
import InfoTransferTab from "./InfoTransferTab";
import InfoKytTravelRuleTab from "./InfoKytTravelRuleTab";
import InfoApprovalTab from "./InfoApprovalTab";
import AuditTrailTab from "./AuditTrailTab";
import RecentActivity from "./RecentActivity";
import { formatCrypto, formatDateTime } from "../../../utils/paymentFormat";
import DetailHeader from "../../../components/PageComponents/DetailHeader";
import { StatusLabel } from "../../../components/Label/status";
import { ProjectField } from "../ProjectField";

const TabsWrapper = styled(Tabs)(() => ``);

const GasStationShow = () => (
  <ShowBase>
    <Content />
  </ShowBase>
);

const Content = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <ComponentShow
      header={
        <DetailHeader
          kind="Gas station transfer"
          title={`${formatCrypto(record.amount)} ${record.currency || ""}`}
          status={<StatusLabel statusField="statusOrder" />}
          facts={[record.internalRef && `Ref. ${record.internalRef}`, <ProjectField />, formatDateTime(record.dateCreated)]}
        />
      }
    >
      <ContentShow />
    </ComponentShow>
  );
};

const ContentShow = () => {
  const { t }: { t: any } = useTranslation();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));
  const record = useRecordContext();
  const { canAccess: canAccessAudit } = useCanAccess({
    action: "list",
    resource: "gasStationTransfersAudit",
  });

  const [currentTab, setCurrentTab] = useState<string>("infoTransfer");

  const tabs = [{ value: "infoTransfer", label: t("Summary") }];
  if (record?.kytRating || record?.travelRuleOwner) {
    tabs.push({ value: "infoKytTravelRule", label: t("KYT & Travel rule") });
  }
  if (record?.approvalRequired) {
    tabs.push({ value: "infoApproval", label: t("Approval") });
  }
  if (canAccessAudit) {
    tabs.push({ value: "auditTrail", label: t("Audit trail") });
  }

  const handleTabsChange = (_event: ChangeEvent<{}>, value: string): void => {
    setCurrentTab(value);
  };

  if (isMobile) {
    return (
      <Box sx={{ px: 2, mt: 2, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <ShowActions resource="gasStationTransfers" />
        </Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("Summary")}
        </Typography>
        <InfoTransferTab />

        {(record?.kytRating || record?.travelRuleOwner) && (
          <>
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              {t("KYT & Travel rule")}
            </Typography>
            <InfoKytTravelRuleTab />
          </>
        )}

        {record?.approvalRequired && (
          <>
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              {t("Approval")}
            </Typography>
            <InfoApprovalTab />
          </>
        )}

        {canAccessAudit && (
          <>
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              {t("Audit trail")}
            </Typography>
            <AuditTrailTab />
          </>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3, mb: 4 }}>
      <Grid container direction="row" justifyContent="center" spacing={4} sx={{ px: 4 }}>
        <Grid item xs={9} md={9}>
          <TabsWrapper
            onChange={handleTabsChange}
            value={currentTab}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </TabsWrapper>
        </Grid>
        <Grid item xs={3} md={3} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <ShowActions resource="gasStationTransfers" />
        </Grid>
        <Grid item xs={12} md={8}>
          {currentTab === "infoTransfer" && <InfoTransferTab />}
          {currentTab === "infoKytTravelRule" && <InfoKytTravelRuleTab />}
          {currentTab === "infoApproval" && <InfoApprovalTab />}
          {currentTab === "auditTrail" && <AuditTrailTab />}
        </Grid>
        <Grid item xs={12} md={4}>
          <RecentActivity />
        </Grid>
      </Grid>
    </Box>
  );
};

export default GasStationShow;
